import { useEffect, useRef, useState } from "react";
import { CommerceDialog } from "../../commerce/CommerceDialog";
import { toast } from "../../../hooks/useToast";
import { mpHabilitado, mercadoPagoEmUso, retornoPendente, concluirConexao, mensagemMP } from "../../../utils/mercadoPago";
import { useStatusProvedor, retornoPagBankPendente, concluirConexaoPagBank, mensagemPagamento } from "../../../utils/pagamentos";
import { useStatusMercadoPago } from "../../pedidos/PixMercadoPago";
import { useAlertasMercadoPago } from "../AlertasMercadoPago";
import { GATEWAYS, gatewayPorId, estadoDoGateway, ocupaLugar } from "./gateways";
import { useConfigPagamento } from "./useConfigPagamento";
import { FluxoConexao, avisarConectado } from "./FluxoConexao";
import { GerenciarGateway } from "./GerenciarGateway";
import { LinhaPix, JanelaPix, JanelaPorquePix } from "./PixManual";
import { LinhaMaquininha, JanelaTaxas, JanelaMaquininhas } from "./Maquininhas";
import { useAlertasPagamentos, JanelaAlertas } from "./Alertas";
import { Icone } from "./Icone";
import "./pagamentos.css";

/* Configurações → Pagamentos: um painel de status das formas de receber.
   Na tela fica só o essencial (quem está conectado, com qual conta, o que dá
   para conectar ou trocar). Explicações, permissões, segurança e taxas abrem
   em janelas, quando o usuário pede. Ajustes simples salvam sozinhos;
   conectar, trocar e desconectar pedem confirmação. */

function IndicadorSalvo({ estado }) {
  const texto = estado === "salvando" ? "Salvando…" : estado === "salvo" ? "✓ Alterações salvas" : estado === "erro" ? "Não foi possível salvar" : "";
  return <span className={`pgw-salvo ${texto ? "visivel" : ""} ${estado}`} role="status" aria-live="polite">{texto}</span>;
}

function Resumo({ atual, varios, mpDesligado, carregando, estadoSalvo }) {
  let dot = "", titulo, detalhe;
  if (carregando) {
    return (
      <div className="pgw-topo" aria-busy="true">
        <span className="pgw-topo-dot" />
        <div className="pgw-topo-texto"><div className="pgw-skel" style={{ height: 14, width: "55%" }} /><div className="pgw-skel" style={{ height: 11, width: "35%", marginTop: 8 }} /></div>
      </div>
    );
  }
  if (!atual) {
    titulo = "Nenhum recebimento online conectado";
    detalhe = "Conecte um provedor para cobrar com Pix e cartão pelos pedidos e pela loja.";
  } else if (atual.estado === "reconectar") {
    dot = "alerta"; titulo = `${atual.gw.nome} precisa ser reconectado`; detalhe = "As cobranças online estão paradas até você reconectar.";
  } else if (mpDesligado) {
    dot = "alerta"; titulo = "Mercado Pago conectado, mas desligado"; detalhe = "A loja e os pedidos usam a chave PIX manual. Ligue em Gerenciar.";
  } else {
    const conta = atual.gw.conta(atual.status);
    dot = "ok"; titulo = `Recebendo online pelo ${atual.gw.nome}`; detalhe = [atual.gw.resumo, conta].filter(Boolean).join(" · ");
  }
  if (varios) { dot = "alerta"; detalhe = "Mais de um provedor conectado: desconecte um deles em Gerenciar."; }
  return (
    <div className="pgw-topo">
      <span className={`pgw-topo-dot ${dot}`} aria-hidden="true" />
      <div className="pgw-topo-texto"><strong>{titulo}</strong><span>{detalhe}</span></div>
      <IndicadorSalvo estado={estadoSalvo} />
    </div>
  );
}

function ItemGateway({ item, atual, concluindo, realce, onConectar, onGerenciar }) {
  const { gw, status, estado } = item;
  const outroAtivo = atual && atual.gw.id !== gw.id;
  const conta = gw.conta(status);
  const classes = ["pgw-item", ocupaLugar(estado) ? "pgw-item--ativo" : "", estado === "em_breve" ? "pgw-item--embreve" : "", realce ? "pgw-realce" : ""].join(" ");

  let statusEl = null;
  if (concluindo) statusEl = <span className="pgw-status"><span className="pgw-spin" aria-hidden="true" /> Conectando…</span>;
  else if (estado === "conectado") statusEl = <span className="pgw-status ok"><i />Conectado</span>;
  else if (estado === "reconectar") statusEl = <span className="pgw-status alerta"><i />Reconexão necessária</span>;
  else if (estado === "em_breve") statusEl = <span className="pgw-tag neutra">Em breve</span>;

  let acoes = null;
  if (estado === "carregando" || concluindo) acoes = <div className="pgw-skel" style={{ width: 96, height: 36 }} aria-hidden="true" />;
  else if (estado === "conectado") acoes = <button type="button" className="pgw-btn" onClick={onGerenciar}>Gerenciar</button>;
  else if (estado === "reconectar") acoes = <>
    <button type="button" className="pgw-btn texto" onClick={onGerenciar}>Detalhes</button>
    <button type="button" className="pgw-btn primario" onClick={onConectar}>Reconectar</button>
  </>;
  else if (estado === "disponivel") acoes = outroAtivo
    ? <button type="button" className="pgw-btn contorno" onClick={onConectar}>Trocar para {gw.nome}</button>
    : <button type="button" className="pgw-btn primario" onClick={onConectar}>{gw.pedeTag ? "Configurar" : "Conectar"}</button>;

  return (
    <li className={classes} style={{ "--gw-cor": gw.cor, "--b-cor": gw.cor }} aria-labelledby={`pgw-gw-${gw.id}`}>
      <div className="pgw-logo" aria-hidden="true">{gw.sigla}</div>
      <div className="pgw-texto">
        <div className="pgw-titulo">
          <span className="pgw-nome" id={`pgw-gw-${gw.id}`}>{gw.nome}</span>
          {statusEl}
          {gw.emTeste() && estado !== "em_breve" && <span className="pgw-tag">Teste</span>}
        </div>
        <div className="pgw-sub">
          {gw.resumo}
          {ocupaLugar(estado) && conta ? <> · <b>{conta}</b></> : null}
          {estado === "disponivel" && outroAtivo ? " · outro provedor está conectado" : null}
        </div>
      </div>
      <div className="pgw-acoes">{acoes}</div>
    </li>
  );
}

function JanelaSeguranca({ onClose }) {
  return (
    <CommerceDialog className="pgw-dialogo" title="Seus dados estão protegidos" onClose={onClose}
      footer={<button type="button" className="pgw-btn" onClick={onClose}>Fechar</button>}>
      <div className="pgw-etapa">
        <p>O Print Control não solicita nem armazena sua senha bancária. As autorizações e os pagamentos são processados diretamente pelos provedores oficiais selecionados. O Print Control recebe apenas as informações e permissões necessárias para criar cobranças e confirmar pagamentos.</p>
        <ul className="pgw-passos">
          <li><span><Icone nome="carteira" /></span><div>O dinheiro cai direto na conta da sua empresa no provedor.</div></li>
          <li><span><Icone nome="chave" /></span><div>Credenciais de acesso ficam só no servidor, cifradas. Nunca no app, no navegador ou no celular.</div></li>
          <li><span><Icone nome="recibo" /></span><div>Um pedido só vira pago depois que o servidor confirma o pagamento na API oficial do provedor, conferindo o valor.</div></li>
          <li><span><Icone nome="escudo" /></span><div>Número do cartão e CVV são digitados só nas páginas dos provedores.</div></li>
        </ul>
      </div>
    </CommerceDialog>
  );
}

export function TelaPagamentos({ uid }) {
  const { cfg, salvar, estado: estadoSalvo, carregando } = useConfigPagamento(uid);
  const statusMP = useStatusMercadoPago(mpHabilitado() ? uid : null);
  const statusPB = useStatusProvedor(uid, "pagbank");
  const statusIP = useStatusProvedor(uid, "infinitepay");
  const alertasMP = useAlertasMercadoPago(mpHabilitado() ? uid : null);
  const alertasPg = useAlertasPagamentos(uid);
  const [janela, setJanela] = useState(null);
  const [concluindo, setConcluindo] = useState(null);
  const [realce, setRealce] = useState(null);
  const retornoTratado = useRef(false);

  const statusDe = { mercadopago: statusMP, pagbank: statusPB, infinitepay: statusIP };
  const itens = GATEWAYS.filter(gw => gw.habilitado() || gw.emBreve())
    .map(gw => ({ gw, status: statusDe[gw.id], estado: estadoDoGateway(gw, statusDe[gw.id]) }));
  const ocupantes = itens.filter(i => ocupaLugar(i.estado));
  const atual = ocupantes[0] || null;
  const carregandoGateways = itens.some(i => i.estado === "carregando");

  function destacar(id) {
    setRealce(id);
    setTimeout(() => setRealce(r => (r === id ? null : r)), 1800);
  }

  // Volta do OAuth (Mercado Pago ou PagBank): conclui no servidor e mostra o resultado
  useEffect(() => {
    if (retornoTratado.current) return;
    retornoTratado.current = true;
    const pendentes = [
      mpHabilitado() && retornoPendente() ? { id: "mercadopago", concluir: () => concluirConexao(retornoPendente()), msg: mensagemMP } : null,
      retornoPagBankPendente() ? { id: "pagbank", concluir: () => concluirConexaoPagBank(retornoPagBankPendente()), msg: mensagemPagamento } : null,
    ].filter(Boolean);
    pendentes.forEach(p => {
      setConcluindo(p.id);
      p.concluir()
        .then(r => { avisarConectado(gatewayPorId(p.id), r); destacar(p.id); })
        .catch(e => toast(p.msg(e), "error", 6000))
        .finally(() => setConcluindo(c => (c === p.id ? null : c)));
    });
  }, []);

  const abrir = (tipo, id) => setJanela({ tipo, id });
  const fechar = () => setJanela(null);
  const itemDe = id => itens.find(i => i.gw.id === id);
  const totalAlertas = alertasMP.length + alertasPg.length;
  const mpEmUso = !carregando && mercadoPagoEmUso(cfg, statusMP);
  const mpDesligado = atual?.gw.id === "mercadopago" && atual.estado === "conectado" && cfg?.mercadoPagoAtivo === false;
  // PIX manual fora dos pedidos enquanto um provedor recebe o Pix (mesma regra de Pedidos)
  const pixPausadoPor = atual?.estado === "conectado" && (atual.gw.id !== "mercadopago" || mpEmUso) ? atual.gw : null;

  return (
    <div className="pgw">
      <Resumo atual={atual} varios={ocupantes.length > 1} mpDesligado={mpDesligado} carregando={carregandoGateways && !atual} estadoSalvo={estadoSalvo} />

      {totalAlertas > 0 && (
        <button type="button" className="pgw-alertas" onClick={() => abrir("alertas")}>
          <Icone nome="alerta" width="18" height="18" style={{ color: "var(--pgw-warn)", flexShrink: 0 }} />
          <strong>{totalAlertas === 1 ? "1 pagamento para conferir" : `${totalAlertas} pagamentos para conferir`}</strong>
          <span>Ver</span>
        </button>
      )}

      <section className="pgw-secao" aria-labelledby="pgw-secao-online">
        <div className="pgw-secao-cab"><h3 id="pgw-secao-online">Recebimento online</h3><small>Um provedor por vez</small></div>
        <ul className="pgw-lista">
          {itens.map(item => (
            <ItemGateway key={item.gw.id} item={item} atual={atual} concluindo={concluindo === item.gw.id} realce={realce === item.gw.id}
              onConectar={() => abrir("conectar", item.gw.id)} onGerenciar={() => abrir("gerenciar", item.gw.id)} />
          ))}
        </ul>
      </section>

      <section className="pgw-secao" aria-labelledby="pgw-secao-outras">
        <div className="pgw-secao-cab"><h3 id="pgw-secao-outras">Outras formas de receber</h3></div>
        {carregando ? (
          <div className="pgw-lista" aria-busy="true" style={{ padding: 16, display: "grid", gap: 12 }}>
            <div className="pgw-skel" style={{ height: 40 }} /><div className="pgw-skel" style={{ height: 40 }} />
          </div>
        ) : (
          <ul className="pgw-lista">
            <LinhaPix cfg={cfg} pausadoPor={pixPausadoPor} salvar={salvar} onEditar={() => abrir("pix")} onPorque={() => abrir("porque")} />
            <LinhaMaquininha cfg={cfg} onVerTaxas={() => abrir("taxas")} onConfigurar={() => abrir("maquininhas")} />
          </ul>
        )}
      </section>

      {!carregando && (
        <section className="pgw-secao" aria-labelledby="pgw-secao-avisos">
          <div className="pgw-secao-cab"><h3 id="pgw-secao-avisos">Notificações de pagamento</h3></div>
          <ul className="pgw-lista">
            <li className="pgw-item" style={{ "--gw-cor": "#f5b301" }}>
              <div className="pgw-logo" aria-hidden="true">R$</div>
              <div className="pgw-texto">
                <div className="pgw-titulo"><span className="pgw-nome" id="pgw-aviso-pagamento">Pagamento recebido</span></div>
                <div className="pgw-sub">Receba um aviso quando o cliente pagar um Pix ou link do pedido. Toque na notificação para conferir a venda.</div>
              </div>
              <div className="pgw-acoes">
                <button type="button" role="switch" className="pgw-switch" aria-checked={cfg.avisoPagamentoRecebido !== false} aria-labelledby="pgw-aviso-pagamento"
                  onClick={() => salvar({ avisoPagamentoRecebido: cfg.avisoPagamentoRecebido === false })} />
              </div>
            </li>
          </ul>
        </section>
      )}

      <div className="pgw-rodape">
        <Icone nome="escudo" />
        <button type="button" className="pgw-link" onClick={() => abrir("seguranca")}>Como protegemos seus dados</button>
      </div>

      {janela?.tipo === "conectar" && (() => {
        const item = itemDe(janela.id);
        if (!item) return null;
        const outro = atual && atual.gw.id !== item.gw.id ? atual : null;
        return <FluxoConexao gw={item.gw} status={item.status} atual={outro} onClose={fechar}
          onConcluido={(gw, r) => { avisarConectado(gw, r); destacar(gw.id); }} />;
      })()}
      {janela?.tipo === "gerenciar" && (() => {
        const item = itemDe(janela.id);
        if (!item || !ocupaLugar(item.estado)) return null;
        return <GerenciarGateway gw={item.gw} status={item.status} estado={item.estado} cfg={cfg} salvarCfg={salvar}
          onReconectar={() => abrir("conectar", item.gw.id)} onClose={fechar} />;
      })()}
      {janela?.tipo === "pix" && <JanelaPix cfg={cfg} salvar={salvar} onClose={fechar} />}
      {janela?.tipo === "porque" && pixPausadoPor && <JanelaPorquePix gw={pixPausadoPor} onClose={fechar} onGerenciar={() => abrir("gerenciar", pixPausadoPor.id)} />}
      {janela?.tipo === "taxas" && <JanelaTaxas cfg={cfg} onClose={fechar} onConfigurar={() => abrir("maquininhas")} />}
      {janela?.tipo === "maquininhas" && <JanelaMaquininhas cfg={cfg} salvar={salvar} onClose={fechar} />}
      {janela?.tipo === "seguranca" && <JanelaSeguranca onClose={fechar} />}
      {janela?.tipo === "alertas" && <JanelaAlertas uid={uid} alertasPg={alertasPg} comMercadoPago={mpHabilitado() && alertasMP.length > 0} onClose={fechar} />}
    </div>
  );
}
